import { OpenChatConversation, OpenChatMessage, OpenChatMessageType, SearchSourcesData } from "@/types/open-chat";
import { OPEN_CHAT_TAGS } from "@/api/open-chat.constraints";
import { baseApi } from "@/api/baseApi";

export const openChatApi = baseApi.injectEndpoints({
  overrideExisting: false,
  endpoints: builder => ({
    getConversations: builder.query<OpenChatConversation[], void>({
      providesTags: result =>
        (result || []).map(row => ({ type: OPEN_CHAT_TAGS.OPEN_CHAT_CONVERSATION_TAG, id: row.id })),
      query: () => ({
        method: "GET",
        url: "/pf-mainweb/open-chat/conversations/",
      }),
    }),

    updateConversation: builder.mutation<OpenChatConversation, { id: string; data: { name: string } }>({
      invalidatesTags: result => [
        result
          ? { type: OPEN_CHAT_TAGS.OPEN_CHAT_CONVERSATION_TAG, id: result.id }
          : { type: OPEN_CHAT_TAGS.OPEN_CHAT_CONVERSATION_TAG, id: "LIST" },
      ],
      query: ({ id, data }) => ({
        method: "PATCH",
        url: `/pf-mainweb/open-chat/conversations/${id}`,
        data,
      }),
    }),

    getMessages: builder.query<OpenChatMessage[], string>({
      providesTags: (_result, _error, conversationId) => [{ type: OPEN_CHAT_TAGS.OPEN_CHAT_TAG, id: conversationId }],
      query: conversationId => ({
        method: "GET",
        url: `/pf-mainweb/open-chat/query/${conversationId}`,
      }),
      transformResponse(response: (OpenChatMessage & { search_sources?: SearchSourcesData })[]) {
        return response.map(message => ({
          ...message,
          completed: true,
          searchSources: message.search_sources ?? message.searchSources,
        }));
      },
    }),
    sendMessage: builder.mutation<
      OpenChatConversation | null,
      {
        conversationId: string;
        message: string;
        messageId: string;
        prompt: "open-chat-prompt-generator" | "open-chat-style-prompt";
        webSearch?: boolean;
      }
    >({
      query: ({ conversationId, message, messageId, prompt, webSearch }) => ({
        url: `/pf-mainweb/open-chat/query/${conversationId}`,
        method: "POST",
        data: { message, message_id: messageId, prompt, web_search: webSearch },
      }),
      async onQueryStarted({ conversationId, message, messageId }, { dispatch, queryFulfilled }) {
        dispatch(
          openChatApi.util.updateQueryData("getMessages", conversationId, (data = []) => [
            ...data,
            { id: messageId, type: "human" as OpenChatMessageType, content: message },
          ]),
        );
        const { data: conversation } = await queryFulfilled;
        if (conversation) {
          dispatch(
            openChatApi.util.updateQueryData("getConversations", undefined, (conversations: OpenChatConversation[]) => {
              if (!conversations.some(row => row.id === conversation.id)) {
                conversations.push(conversation);
              }
            }),
          );
        }
      },
    }),
    cancelMessage: builder.mutation<void, { conversationId: string }>({
      query: ({ conversationId }) => ({
        url: `/pf-mainweb/open-chat/cancel/${conversationId}`,
        method: "POST",
      }),
      async onQueryStarted({ conversationId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          openChatApi.util.updateQueryData("getMessages", conversationId, drafts => {
            const lastAiMessage = [...drafts].reverse().find(d => d.type === "ai");
            if (lastAiMessage && !lastAiMessage.completed) {
              lastAiMessage.completed = true;
              lastAiMessage.isSearching = false;
            }
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});

export const {
  useSendMessageMutation,
  useGetMessagesQuery,
  useGetConversationsQuery,
  useUpdateConversationMutation,
  useCancelMessageMutation,
} = openChatApi;
